/**
 * @file TaskPackButton.tsx
 * @description 任务包预下载按钮 + 进度 Modal。
 *
 * 用户在任务详情页点击「下載離線包」，弹出 Modal 显示：
 *   - 已缓存表單 schema 数 / DOCX 模板进度 / 附件进度
 *   - 完成后：「已下載 N 張表單 + K 個 DOCX 模板 + L 個附件」
 *   - 配额不足时：「空间不足，已下载部分」+ 跳设置/清理提示
 *
 * 文案要点：避免单一"X 個附件"误导用户。即使 docx/附件都为 0（仅有 schema），也要明确
 * 显示"已快取 N 張表單 schema"，让用户知道离线时表单可填写。
 */

import { useState } from 'react';
import { Button, Modal, Progress, Alert, Typography, Space } from 'antd';
import { message } from '@/lib/antd-app';
import { Download } from 'lucide-react';
import { prefetchTaskFull, type PrefetchProgress, type PrefetchResult } from '@/pwa/task-pack';

interface Props {
  taskId: number;
  /** 完成后回调（可选，用于父层刷新 useFileMetas 缓存） */
  onComplete?: (result: PrefetchResult) => void;
  size?: 'small' | 'middle' | 'large';
}

/** scope → 中文文案 */
const SCOPE_LABEL: Record<NonNullable<PrefetchProgress['currentScope']>, string> = {
  'form-template-docx': 'DOCX 模板',
  'form-attachment': '附件',
  'case-document': '案件文件',
};

export function TaskPackButton({ taskId, onComplete, size }: Props) {
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<PrefetchProgress>({ downloaded: 0, total: 0 });
  const [result, setResult] = useState<PrefetchResult | null>(null);

  const handleStart = async () => {
    setRunning(true);
    setResult(null);
    setProgress({ downloaded: 0, total: 0 });
    try {
      const res = await prefetchTaskFull(taskId, (p) => setProgress(p));
      setResult(res);
      onComplete?.(res);
      if (res.errors.length === 0) {
        void message.success(
          `任務包已下載：${res.forms} 張表單、${res.caseDocuments.downloaded} 個案件文件、${res.attachments.downloaded} 個附件`,
        );
      } else {
        void message.warning(`下載完成，但有 ${res.errors.length} 個錯誤`);
      }
    } catch (err) {
      console.error('[task-pack-button] failed', err);
      const msg = err instanceof Error ? err.message : String(err);
      void message.error(`下載失敗：${msg}`);
    } finally {
      setRunning(false);
    }
  };

  // 进度条仅基于二进制下载（DOCX + 附件）；二进制总数为 0 时
  // 只要 schema 已经写入即视为完成（pct=100，给个对勾）。
  const pct =
    progress.total > 0
      ? Math.round((progress.downloaded / progress.total) * 100)
      : result
        ? 100
        : 0;

  return (
    <>
      <Button size={size} icon={<Download size={14} />} onClick={() => setOpen(true)}>
        下載離線包
      </Button>

      <Modal
        title="下載任務離線包"
        open={open}
        onCancel={() => !running && setOpen(false)}
        maskClosable={!running}
        footer={
          result ? (
            <Button onClick={() => setOpen(false)}>關閉</Button>
          ) : (
            <Space>
              <Button onClick={() => setOpen(false)} disabled={running}>
                取消
              </Button>
              <Button type="primary" loading={running} onClick={() => void handleStart()}>
                開始下載
              </Button>
            </Space>
          )
        }
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            點擊「開始下載」後會把任務、案件文件、客戶資料、表單 schema、DOCX
            模板、歷史附件與現場資料下載到本地，現場離線時可查看與填寫。
          </Typography.Paragraph>

          {(running || result) && (
            <>
              <Progress percent={pct} status={running ? 'active' : 'success'} />
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                {result && (
                  <>
                    <Typography.Text style={{ fontSize: 12 }}>
                      任務：{result.task.cached ? '已快取' : '未快取'} · 案件：
                      {result.caseDetail.cached ? '已快取' : '未快取'}
                    </Typography.Text>
                    <Typography.Text style={{ fontSize: 12 }}>
                      表單 schema：<b>{result.forms}</b> 張 · 客戶資料：
                      {result.clientProfile.cached} / {result.clientProfile.total} · 現場資料：
                      {result.fieldData.cached} / {result.fieldData.total}
                    </Typography.Text>
                  </>
                )}
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  DOCX 模板：
                  {result
                    ? `${result.docxFiles.downloaded + result.docxFiles.skipped} / ${result.docxFiles.total}`
                    : '—'}
                  {' · '}
                  案件文件：
                  {result
                    ? `${result.caseDocuments.downloaded + result.caseDocuments.skipped} / ${result.caseDocuments.total}`
                    : '—'}
                  {' · '}
                  附件：
                  {result
                    ? `${result.attachments.downloaded + result.attachments.skipped} / ${result.attachments.total}`
                    : '—'}
                </Typography.Text>
                {progress.currentFileId && progress.currentScope && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    當前下載：{SCOPE_LABEL[progress.currentScope]} #{progress.currentFileId}
                  </Typography.Text>
                )}
              </Space>
            </>
          )}

          {result?.stoppedAtQuota && (
            <Alert
              type="warning"
              showIcon
              message="儲存空間不足"
              description="已下載部分；請在「設置 → 離線存儲」清理舊任務後重試。"
            />
          )}
          {result && result.errors.length > 0 && (
            <Alert
              type="error"
              showIcon
              message={`${result.errors.length} 個錯誤`}
              description={
                <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                  {result.errors.slice(0, 5).map((e, i) => (
                    <li key={i} style={{ fontSize: 12 }}>
                      {e}
                    </li>
                  ))}
                  {result.errors.length > 5 && (
                    <li style={{ fontSize: 12 }}>…還有 {result.errors.length - 5} 個</li>
                  )}
                </ul>
              }
            />
          )}
        </Space>
      </Modal>
    </>
  );
}
